import { useState, type FormEvent } from "react";
import { Navigate, useSearchParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, ApiError } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import "./credits.css";

interface LookupResponse {
  user: {
    id: string;
    email: string;
    role: "user" | "admin";
    points: number;
    createdAt: string;
  };
  transactions: Array<{
    id: string;
    delta: number;
    reason: string;
    bookTitle: string | null;
    createdAt: string;
  }>;
}

export function AdminCredits() {
  const { t, i18n } = useTranslation();
  const { user, loading } = useAuth();
  const qc = useQueryClient();
  // Allow drill-down entry from the Stats page: /admin/creditos?email=foo@bar
  // preloads that user so Grace can adjust credits in one click.
  const [searchParams] = useSearchParams();
  const preset = searchParams.get("email") ?? "";
  const [searchEmail, setSearchEmail] = useState(preset);
  const [submittedEmail, setSubmittedEmail] = useState(preset.toLowerCase());
  const [delta, setDelta] = useState("");
  const [flash, setFlash] = useState<{ type: "ok" | "err"; msg: string } | null>(null);

  const lookup = useQuery<LookupResponse, ApiError>({
    queryKey: ["admin", "user-lookup", submittedEmail],
    queryFn: () =>
      api<LookupResponse>(`/api/admin/user-lookup?email=${encodeURIComponent(submittedEmail)}`),
    enabled: submittedEmail.length > 0,
    retry: false,
  });

  const grant = useMutation({
    mutationFn: (input: { userEmail: string; delta: number }) =>
      api<{ newBalance: number }>("/api/admin/credits", { method: "POST", body: input }),
    onSuccess: (res) => {
      setFlash({ type: "ok", msg: t("admin.credits.balanceUpdated", { count: res.newBalance }) });
      setDelta("");
      qc.invalidateQueries({ queryKey: ["admin", "user-lookup", submittedEmail] });
    },
    onError: (err: ApiError) => setFlash({ type: "err", msg: err.message }),
  });

  if (loading) return <div className="container"><p>{t("common.loading")}</p></div>;
  if (!user || user.role !== "admin") return <Navigate to="/login" replace />;

  function onSearch(e: FormEvent) {
    e.preventDefault();
    setFlash(null);
    const email = searchEmail.trim().toLowerCase();
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setFlash({ type: "err", msg: t("admin.credits.emailInvalid") });
      return;
    }
    setSubmittedEmail(email);
  }

  function onAdjust(e: FormEvent) {
    e.preventDefault();
    setFlash(null);
    const n = parseInt(delta, 10);
    if (!Number.isFinite(n) || n === 0) {
      setFlash({ type: "err", msg: t("admin.credits.nonZero") });
      return;
    }
    grant.mutate({ userEmail: submittedEmail, delta: n });
  }

  return (
    <section aria-labelledby="credits-title" className="admin__panel credits">
      <h2 id="credits-title">{t("admin.credits.title")}</h2>
      <p>
        {t("admin.credits.intro")}{" "}
        <em>{t("admin.credits.reason.admin_grant")}</em>.
      </p>

      {flash && (
        <div className={`credits__flash credits__flash--${flash.type}`} role="alert" aria-live="polite">
          {flash.msg}
        </div>
      )}

      <form onSubmit={onSearch} className="surface credits__search">
        <div className="field">
          <label htmlFor="search-email">{t("admin.credits.emailLabel")}</label>
          <input
            id="search-email"
            type="email"
            autoComplete="email"
            placeholder={t("admin.credits.emailPlaceholder")}
            value={searchEmail}
            onChange={(e) => setSearchEmail(e.target.value)}
            required
          />
        </div>
        <button type="submit" className="btn btn-primary" disabled={lookup.isFetching}>
          {lookup.isFetching ? t("admin.credits.searching") : t("admin.credits.search")}
        </button>
      </form>

      {lookup.error && (
        <p className="field__error" role="alert">{lookup.error.message}</p>
      )}

      {lookup.data && (
        <div className="surface credits__user">
          <div className="credits__user-head">
            <h3>{lookup.data.user.email}</h3>
            <p className="credits__balance">
              <span className="credits__balance-label">{t("admin.credits.balance")}</span>
              <strong>{lookup.data.user.points}</strong>
            </p>
          </div>

          <form onSubmit={onAdjust} className="credits__adjust">
            <div className="field">
              <label htmlFor="delta">{t("admin.credits.deltaLabel")}</label>
              <input
                id="delta"
                type="number"
                step="1"
                inputMode="numeric"
                placeholder={t("admin.credits.deltaPlaceholder")}
                value={delta}
                onChange={(e) => setDelta(e.target.value)}
                required
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={grant.isPending}>
              {grant.isPending ? t("admin.credits.applying") : t("admin.credits.apply")}
            </button>
          </form>

          <h4 className="credits__history-title">{t("admin.credits.historyTitle")}</h4>
          {lookup.data.transactions.length === 0 ? (
            <p className="credits__empty">{t("admin.credits.empty")}</p>
          ) : (
            <ul className="credits__history">
              {lookup.data.transactions.map((tx) => (
                <li key={tx.id}>
                  <span className={`credits__delta credits__delta--${tx.delta >= 0 ? "pos" : "neg"}`}>
                    {tx.delta >= 0 ? "+" : ""}{tx.delta}
                  </span>
                  <span className="credits__reason">
                    {t(`admin.credits.reason.${tx.reason}`, { defaultValue: tx.reason })}
                    {tx.bookTitle && (
                      <>
                        {" — "}<em>{tx.bookTitle}</em>
                      </>
                    )}
                  </span>
                  <time dateTime={tx.createdAt}>
                    {new Date(tx.createdAt).toLocaleDateString(i18n.language || "pt-PT")}
                  </time>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
